import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  getFormattedStatsCount,
  handleErrorMessage,
  openSuccessNotification,
} from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { CampaignTemplateType, ViewEmailType } from "models/interfaces";
import { fetchEmailContent } from "screens/Campaigns/CampaignService";
import parse from "html-react-parser";
import { isEmpty } from "lodash";
import UserTemplateModal from "screens/Templates/Components/UserTemplateModal";
import urls from "global/constants/UrlConstants";
import { addCampaignTemplate } from "screens/Templates/TemplatesService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import SuggestionBox from "screens/Shared/SuggestionBox";

interface CustomProps {
  id: number;
  requestedCount?: number;
  containsParentId?: boolean;
}
const ViewEmail = (props: CustomProps) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [content, setContent] = useState<ViewEmailType>({
    id: 0,
    formEmail: "",
    senderEmail: "",
    replyToEmail: "",
    domainVerified: false,
    subject: "",
    content: "",
    groupNames: [],
    tagNames: [],
    groupTagNames: [],
    attachments: {},
    contentJson: "",
  });
  const [openTemplateNameModel, setOpenTemplateNameModel] =
    useState<boolean>(false);
  const [formField, setFormField] = useState<any>({
    templateName: {
      value: "",
    },
    category: {
      value: "Private",
    },
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetchEmailContent(props.id);
      setContent(response);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleOnchange = (event: any) => {
    if (event.target.value !== undefined && event.target.value.length <= 40) {
      setFormField({
        ...formField,
        [event.target.name]: {
          ...formField[event.target.name],
          value: event.target.value,
          error: "",
        },
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const template: CampaignTemplateType = {
        category: formField.category.value,
        name: formField.templateName.value.trim(),
        jsonContent: content.contentJson ? content.contentJson : "",
        customTools: [urls.customSocialTool, urls.customVideoTool],
      };

      if (template.name && template.jsonContent) {
        const updateTemplate = await addCampaignTemplate(template);
        openSuccessNotification(updateTemplate.message);
      }
      setOpenTemplateNameModel(false);
      setIsLoading(false);
      setFormField({
        templateName: {
          value: "",
        },
        category: {
          value: "Private",
        },
      });
    } catch (error: any) {
      setOpenTemplateNameModel(false);
      setIsLoading(false);
      handleErrorMessage(error);
    } finally {
      setOpenTemplateNameModel(false);
      setIsLoading(false);
      setFormField({
        templateName: {
          value: "",
        },
        category: {
          value: "Private",
        },
      });
    }
  };

  const getBody = () => {
    return (
      <Box p={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={isDesktop ? "self-end" : "start"}
          spacing={2}
          flexDirection={isDesktop ? "row" : "column-reverse"}
        >
          <Typography>
            <span style={{ fontWeight: 700 }}>From: </span>
            {content.senderEmail ? content.senderEmail : content.formEmail}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setOpenTemplateNameModel(true)}
            sx={{ padding: "1px 10px", marginLeft: "0px !important" }}
          >
            Save as template
          </Button>
        </Stack>
        {content.replyToEmail && (
          <Typography>
            <span style={{ fontWeight: 700 }}>Reply To: </span>
            {content.replyToEmail}
          </Typography>
        )}
        <Typography>
          <span style={{ fontWeight: 700 }}>To: </span>
          {props.containsParentId!
            ? `${getFormattedStatsCount(props.requestedCount!)} recipients`
            : content.groupTagNames.join(", ")}
        </Typography>
        <Typography>
          <span style={{ fontWeight: 700 }}>Subject: </span>
          {content.subject}
        </Typography>
        <Typography gutterBottom>
          <span style={{ fontWeight: 700 }}>Attachment: </span>
          {!isEmpty(content.attachments)
            ? Object.keys(content.attachments)
                .map((key) => key)
                .join(", ")
            : "None"}
        </Typography>
        {!content.domainVerified && (
          <Stack pb={1.5}>
            <SuggestionBox
              title={`The sender domain ${
                content.senderEmail
                  ? content.senderEmail.split("@")[1]
                  : content.formEmail.split("@")[1]
              } is not authenticated. To maintain reliable email delivery, the emails have been sent from @trywithmailzzy.com instead.`}
            />
          </Stack>
        )}
        {parse(content.content)}
      </Box>
    );
  };

  return (
    <Container maxWidth="md">
      {loading ? (
        <Skeleton animation="wave" variant="rounded" height={500} />
      ) : (
        <CenteredCardLayout>{getBody()}</CenteredCardLayout>
      )}
      {openTemplateNameModel && (
        <UserTemplateModal
          isVisible={openTemplateNameModel}
          handleDialogClose={() => {
            setOpenTemplateNameModel(false);
            setFormField({
              templateName: {
                value: "",
              },
              category: {
                value: "Private",
              },
            });
          }}
          title={"Enter template name"}
          id="templates_name_field"
          value={formField.templateName.value}
          name={"templateName"}
          onChange={(event: any) => handleOnchange(event)}
          lengthOfValue={formField.templateName?.value?.length}
          btn1Title="Cancel"
          btn1OnClick={() => {
            setOpenTemplateNameModel(false);
            setFormField({
              templateName: {
                value: "",
              },
              category: {
                value: "Private",
              },
            });
          }}
          btn2Title="Save"
          btn2OnClick={() => handleSave()}
        />
      )}
      <CustomLoader isLoading={isLoading} />
    </Container>
  );
};

export default ViewEmail;
